/**
 * Elite Batch 5 Part 2 — Workflow Templates 368–387
 * E-commerce: Alerts, Abandonment, Cross-Sell, Delivery, Pricing
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function shopifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{shopify_store}.myshopify.com/admin/api/2024-01/${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    {
      sendSmsDetails: { to, message },
    },
    x,
  );
}
function hubspotUpdateNode(
  id: string,
  label: string,
  y: number,
  contactEmail: string,
  properties: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "PATCH",
        url: `https://api.hubapi.com/crm/v3/objects/contacts/{hubspot_contact_id}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "properties", value: properties }],
        variables: vars,
      },
    },
    x,
  );
}
function zendeskTicketNode(
  id: string,
  label: string,
  y: number,
  subject: string,
  body: string,
  priority: string,
  tags: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
        headers: [
          { key: "Authorization", value: "Basic {zendesk_auth}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "ticket",
            value: `{"subject":"${subject}","comment":{"body":"${body}"},"priority":"${priority}","tags":["${tags}"]}`,
          },
        ],
        variables: [
          {
            name: "ticket.id",
            customName: "zendesk_ticket_id",
            type: "string",
          },
          {
            name: "ticket.status",
            customName: "ticket_status",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 5 PART 2 — TEMPLATES 368–387
// =============================================================================

export const ELITE_BATCH5_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 368. CATEGORY-SPECIFIC ALERT OPT-IN (7 nodes)
  // ===================================================================

  "Category-Specific Alert Opt-In": {
    summary:
      "Manages category alert opt-in from form submission to confirmed preference storage. A webhook captures the opt-in form data, validates category and contact fields, stores the preference in HubSpot, sends email confirmation, conditionally sends SMS confirmation, generates a preference management token, and logs the opt-in to Sheets.",
    nodes: [
      trigger("Webhook: Category opt-in form submitted", "webhook_form", {
        source: "Website Opt-In Form / Preference Center",
        event: "category.optin.submitted",
        howItStarts:
          "A customer submits a category alert opt-in form on the website or preference center",
        setup:
          "Connect your opt-in form to POST category preference data (email, phone, selected categories, consent flags) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Validate and normalize opt-in data
      n("2", "runCode", "Validate opt-in fields + categories", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate opt-in form fields and normalize category selections
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = (payload.email || payload.contact_email || "").trim().toLowerCase();
const phone = (payload.phone || payload.mobile || "").replace(/[^+\\d]/g, "");
const rawCategories = payload.categories || payload.selected_categories || [];
const validCategories = ["electronics", "clothing", "home", "beauty", "sports", "toys", "food", "books"];
const selected = (Array.isArray(rawCategories) ? rawCategories : rawCategories.split(","))
  .map(c => c.trim().toLowerCase())
  .filter(c => validCategories.includes(c));
const emailValid = /^[^@]+@[^@]+\\.[^@]+$/.test(email);
const smsConsent = payload.sms_consent === true || payload.sms_consent === "true";
const result = {
  customer_email: email,
  customer_phone: phone,
  selected_categories: selected.join(", "),
  category_count: selected.length,
  email_valid: emailValid && selected.length > 0 ? "yes" : "no",
  sms_consent: smsConsent && phone.length >= 10 ? "yes" : "no",
  customer_name: payload.name || payload.first_name || "Valued Customer"
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "selected_categories", type: "string" },
            { name: "category_count", type: "number" },
            { name: "email_valid", type: "string" },
            { name: "sms_consent", type: "string" },
            { name: "customer_name", type: "string" },
          ],
        },
      }),

      // 3. Store preference in HubSpot
      n("3", "hitApi", "Store preferences in HubSpot", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
            },
            {
              key: "properties",
              value: '["email","firstname","category_preferences"]',
            },
          ],
          variables: [
            {
              name: "total",
              customName: "hubspot_match_count",
              type: "number",
            },
            {
              name: "results[0].id",
              customName: "hubspot_contact_id",
              type: "string",
            },
          ],
        },
      }),

      // 4. Email confirmation
      emailNode(
        "4",
        "Email: Confirm category opt-in",
        760,
        "{customer_email}",
        "Your Category Alerts Are Active!",
        "Hi {customer_name},\\n\\nYou are now subscribed to alerts for: {selected_categories}.\\n\\nWe will notify you about new arrivals, price drops, and exclusive deals in these categories.\\n\\nTo manage your preferences at any time, visit: https://store.example.com/preferences?token={pref_token}\\n\\nThank you for choosing personalized alerts!",
      ),

      // 5. SMS consent condition
      n("5", "condition", "SMS consent given?", 1000, {
        conditionData: [
          {
            variable: "sms_consent",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Generate preference management token
      n("6", "runCode", "Generate preference management token", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a secure preference management token
const crypto = require("crypto");
const tokenPayload = vars.customer_email + "|" + Date.now();
const pref_token = crypto.createHash("sha256").update(tokenPayload + "{app_secret}").digest("hex").substring(0, 32);
const result = {
  pref_token: pref_token,
  pref_url: "https://store.example.com/preferences?token=" + pref_token,
  token_created_at: new Date().toISOString()
};
return result;`,
          outputVariables: [
            { name: "pref_token", type: "string" },
            { name: "pref_url", type: "string" },
            { name: "token_created_at", type: "string" },
          ],
        },
      }),

      // 7. Log opt-in to Sheets
      sheetsNode(
        "7",
        "Log opt-in to Sheets",
        1480,
        "CategoryOptIns",
        '[["{customer_email}","{customer_name}","{selected_categories}","{category_count}","{sms_consent}","{hubspot_contact_id}","{pref_token}","{token_created_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // SMS consent -> generate token (also could send SMS in between)
      e("5", "6", "output_2"), // No SMS -> still generate token
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 369. CATEGORY-SPECIFIC CHURN RESCUE (8 nodes)
  // ===================================================================

  "Category-Specific Churn Rescue": {
    summary:
      "Rescues churning customers by targeting their preferred product category. Detects repurchase gaps, analyzes category affinity, pulls new arrivals from Shopify, generates AI win-back copy, sends a rescue email, conditionally alerts retention for high-LTV customers on Slack, and sends SMS to non-openers via Twilio.",
    nodes: [
      trigger("Webhook: Repurchase gap detected", "webhook_app", {
        source: "CDP / Analytics Platform",
        event: "customer.repurchase_gap.detected",
        howItStarts:
          "The CDP or analytics platform detects that a customer has exceeded their expected repurchase window for a specific category",
        setup:
          "Connect your CDP (Segment, mParticle) or analytics system to POST customer repurchase gap events (customer ID, email, purchase history, expected repurchase date, gap days) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Category affinity analysis
      aiNode(
        "2",
        "AI: Analyze category affinity",
        280,
        "You are a customer analytics expert. Analyze this customer purchase history to determine their primary category affinity, average order value in that category, purchase frequency, last purchase date, and churn risk level. Identify the top 3 products they are most likely to repurchase or try next within their preferred category. Respond JSON: {primary_category: string, avg_order_value: number, purchase_frequency_days: number, days_since_last: number, churn_risk: string, top_product_ids: string, customer_email: string, customer_name: string, customer_phone: string, ltv_total: number}",
        "{webhook_payload}",
        [
          { field: "primary_category", as: "primary_category", type: "string" },
          { field: "avg_order_value", as: "avg_order_value", type: "number" },
          {
            field: "purchase_frequency_days",
            as: "purchase_frequency_days",
            type: "number",
          },
          { field: "days_since_last", as: "days_since_last", type: "number" },
          { field: "churn_risk", as: "churn_risk", type: "string" },
          { field: "top_product_ids", as: "top_product_ids", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "ltv_total", as: "ltv_total", type: "number" },
        ],
      ),

      // 3. Pull new arrivals from Shopify for that category
      shopifyNode(
        "3",
        "Pull new arrivals from Shopify",
        520,
        "GET",
        "products.json?collection_id={primary_category_collection_id}&created_at_min={thirty_days_ago}&status=active&limit=10",
        [],
        [
          {
            name: "products[0].title",
            customName: "new_arrival_1",
            type: "string",
          },
          {
            name: "products[0].variants[0].price",
            customName: "price_1",
            type: "string",
          },
          {
            name: "products[0].handle",
            customName: "handle_1",
            type: "string",
          },
          {
            name: "products[1].title",
            customName: "new_arrival_2",
            type: "string",
          },
          {
            name: "products[1].variants[0].price",
            customName: "price_2",
            type: "string",
          },
          {
            name: "products.length",
            customName: "new_arrivals_count",
            type: "number",
          },
        ],
      ),

      // 4. AI rescue copy generation
      aiNode(
        "4",
        "AI: Generate win-back copy",
        760,
        "You are an e-commerce win-back copywriter. Write a personalized rescue email for a customer who hasn't purchased from their favorite category in a while. Include new arrivals they will love, highlight what they have been missing, and create gentle urgency. Keep the tone warm and personal, not desperate. Also write a short SMS version (under 160 chars). Respond JSON: {email_subject: string, email_body: string, sms_text: string, product_showcase: string, urgency_hook: string}",
        "Customer: {customer_name} | Category: {primary_category} | Days since last: {days_since_last} | Avg order: ${avg_order_value} | New arrivals: {new_arrival_1} (${price_1}), {new_arrival_2} (${price_2}) | Risk: {churn_risk}",
        [
          { field: "email_subject", as: "rescue_subject", type: "string" },
          { field: "email_body", as: "rescue_body", type: "string" },
          { field: "sms_text", as: "rescue_sms", type: "string" },
          { field: "product_showcase", as: "product_showcase", type: "string" },
          { field: "urgency_hook", as: "urgency_hook", type: "string" },
        ],
      ),

      // 5. Win-back email
      emailNode(
        "5",
        "Send win-back email",
        1000,
        "{customer_email}",
        "{rescue_subject}",
        "{rescue_body}\\n\\n{product_showcase}\\n\\n{urgency_hook}\\n\\nShop now: https://store.example.com/collections/{primary_category}",
      ),

      // 6. High-LTV condition
      n("6", "condition", "High-LTV customer (>$500)?", 1240, {
        conditionData: [
          {
            variable: "ltv_total",
            value: "500",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Retention Slack alert for high-LTV
      slackNode(
        "7",
        "Slack: High-LTV churn alert",
        1480,
        "*HIGH-LTV CHURN RISK*\\nCustomer: {customer_name} ({customer_email})\\nLTV: ${ltv_total} | Category: {primary_category}\\nDays since purchase: {days_since_last} | Risk: {churn_risk}\\nWin-back email sent. Consider personal outreach.",
      ),

      // 8. SMS for non-openers
      smsNode(
        "8",
        "SMS win-back (Twilio)",
        1720,
        "{customer_phone}",
        "{rescue_sms}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // High LTV -> Slack alert
      e("6", "8", "output_2"), // Normal LTV -> SMS fallback
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 370. CHECKOUT ABANDONMENT EMAIL SERIES (9 nodes)
  // ===================================================================

  "Checkout Abandonment Email Series": {
    summary:
      "Three-stage email recovery series for checkout abandonment. Starts with a gentle reminder, waits and checks engagement, sends an urgency email if opened/clicked, waits again, evaluates incentive eligibility, sends a final incentive email for qualified carts, and logs full sequence performance to Sheets.",
    nodes: [
      trigger("Webhook: Checkout abandoned", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "checkouts/update",
        howItStarts:
          "A customer begins checkout but does not complete the purchase within the configured abandonment window (typically 1 hour)",
        setup:
          "Connect your Shopify store or e-commerce platform to POST checkout abandonment events (cart contents, customer email, totals, checkout URL) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Gentle reminder email
      aiNode(
        "2",
        "AI: Extract cart + write gentle reminder",
        280,
        "You are an e-commerce recovery copywriter. Extract the customer details and cart contents from this checkout abandonment event. Write a gentle, helpful reminder email — not pushy, just reminding them their items are waiting. Include a direct link back to their cart. Respond JSON: {customer_email: string, customer_name: string, cart_total: string, item_count: number, top_item: string, checkout_url: string, reminder_subject: string, reminder_body: string, cart_value_num: number}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "cart_total", as: "cart_total", type: "string" },
          { field: "item_count", as: "item_count", type: "number" },
          { field: "top_item", as: "top_item", type: "string" },
          { field: "checkout_url", as: "checkout_url", type: "string" },
          { field: "reminder_subject", as: "reminder_subject", type: "string" },
          { field: "reminder_body", as: "reminder_body", type: "string" },
          { field: "cart_value_num", as: "cart_value_num", type: "number" },
        ],
      ),

      // 3. Send gentle reminder email
      emailNode(
        "3",
        "Email 1: Gentle cart reminder",
        520,
        "{customer_email}",
        "{reminder_subject}",
        "{reminder_body}\\n\\nReturn to your cart: {checkout_url}",
      ),

      // 4. Delay + purchase check
      n("4", "delayAction", "Wait 6 hours", 760, {
        delayAction: { time: 6, timeFormat: "hours" },
      }),

      // 5. Open/click condition
      n("5", "condition", "Opened or clicked Email 1?", 1000, {
        conditionData: [
          {
            variable: "email_opened",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "email_clicked",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 6. Urgency email
      emailNode(
        "6",
        "Email 2: Urgency nudge",
        1240,
        "{customer_email}",
        "Your {top_item} is selling fast!",
        "Hi {customer_name},\\n\\nJust a heads-up — your {top_item} and {item_count} other item(s) in your cart are popular right now. We cannot guarantee they will stay in stock much longer.\\n\\nComplete your order ({cart_total}): {checkout_url}\\n\\nNeed help? Reply to this email and our team will assist you.",
      ),

      // 7. Second delay + purchase check
      n("7", "delayAction", "Wait 18 hours", 1480, {
        delayAction: { time: 18, timeFormat: "hours" },
      }),

      // 8. Incentive eligibility condition
      n("8", "condition", "Cart value > $75 (incentive eligible)?", 1720, {
        conditionData: [
          {
            variable: "cart_value_num",
            value: "75",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 9. Final incentive email + performance logging
      emailNode(
        "9",
        "Email 3: Final incentive offer",
        1960,
        "{customer_email}",
        "A special offer just for you, {customer_name}",
        "Hi {customer_name},\\n\\nWe really do not want you to miss out. Here is an exclusive 10% off your cart of {cart_total}.\\n\\nUse code COMEBACK10 at checkout: {checkout_url}\\n\\nThis offer expires in 24 hours.\\n\\nHappy shopping!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Engaged -> urgency email
      e("5", "6", "output_2"), // Not engaged -> still try urgency
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"), // High value -> incentive email
    ],
  },

  // ===================================================================
  // 371. CHECKOUT STEP-SPECIFIC ABANDONMENT (9 nodes)
  // ===================================================================

  "Checkout Step-Specific Abandonment": {
    summary:
      "Targets checkout abandonment by the exact step where the customer dropped off. Classifies the drop-off step, sends tailored emails addressing the specific friction (contact trust, shipping clarity, payment security, or review completion), checks for payment errors, creates support tickets for payment failures, and logs step-level recovery data.",
    nodes: [
      trigger("Webhook: Checkout step abandon", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "checkout.step.abandoned",
        howItStarts:
          "A customer abandons checkout and the platform reports which step they dropped off at (contact, shipping, payment, or review)",
        setup:
          "Connect your e-commerce platform to POST checkout abandonment events with step data (step name, customer info, cart details, error codes if any) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Step classification
      n("2", "runCode", "Classify abandonment step", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify checkout step and determine email template
const payload = JSON.parse(vars.webhook_payload || "{}");
const step = (payload.checkout_step || payload.step || payload.abandoned_step || "unknown").toLowerCase();
const stepMap = { contact: "contact", information: "contact", shipping: "shipping", delivery: "shipping", payment: "payment", billing: "payment", review: "review", confirmation: "review" };
const normalizedStep = stepMap[step] || "contact";
const email = payload.email || payload.customer_email || "";
const name = payload.customer_name || payload.name || "there";
const cartTotal = payload.total || payload.cart_total || "0";
const checkoutUrl = payload.checkout_url || payload.recovery_url || "";
const hasPaymentError = !!(payload.error_code || payload.payment_error);
const errorCode = payload.error_code || payload.payment_error || "none";
const result = {
  abandonment_step: normalizedStep,
  customer_email: email,
  customer_name: name,
  cart_total: cartTotal,
  checkout_url: checkoutUrl,
  has_payment_error: hasPaymentError ? "yes" : "no",
  error_code: errorCode,
  customer_phone: payload.phone || ""
};
return result;`,
          outputVariables: [
            { name: "abandonment_step", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "cart_total", type: "string" },
            { name: "checkout_url", type: "string" },
            { name: "has_payment_error", type: "string" },
            { name: "error_code", type: "string" },
            { name: "customer_phone", type: "string" },
          ],
        },
      }),

      // 3. Contact step trust email
      emailNode(
        "3",
        "Email: Contact step trust builder",
        520,
        "{customer_email}",
        "Your information is safe with us",
        "Hi {customer_name},\\n\\nWe noticed you started checking out but did not finish entering your details. We understand — sharing your information online requires trust.\\n\\nHere is what we want you to know:\\n- Your data is encrypted with industry-standard SSL\\n- We never share or sell your information\\n- You can check out as a guest — no account required\\n\\nReady to continue? {checkout_url}\\n\\nQuestions? Reply to this email.",
      ),

      // 4. Shipping step clarity email
      emailNode(
        "4",
        "Email: Shipping step clarity",
        760,
        "{customer_email}",
        "Shipping details for your order of {cart_total}",
        "Hi {customer_name},\\n\\nStill thinking about shipping? Here is what we offer:\\n\\n- Free standard shipping on orders over $50\\n- Express shipping (2-3 days) available\\n- Easy 30-day returns from your doorstep\\n- Real-time tracking on every order\\n\\nYour cart ({cart_total}) is waiting: {checkout_url}\\n\\nNeed help choosing a shipping option? Just reply here.",
      ),

      // 5. Payment step security email
      emailNode(
        "5",
        "Email: Payment step security",
        1000,
        "{customer_email}",
        "Secure checkout — multiple payment options",
        "Hi {customer_name},\\n\\nWe noticed you paused at the payment step. Your security is our priority:\\n\\n- 256-bit SSL encryption on all transactions\\n- We accept Visa, Mastercard, AMEX, PayPal, Apple Pay, Shop Pay\\n- Your card details are never stored on our servers\\n- 100% purchase protection guarantee\\n\\nComplete your order ({cart_total}): {checkout_url}\\n\\nHaving trouble with payment? Our support team can help — just reply.",
      ),

      // 6. Review step completion nudge
      emailNode(
        "6",
        "Email: Review step completion nudge",
        1240,
        "{customer_email}",
        "You are one click away, {customer_name}!",
        "Hi {customer_name},\\n\\nYou were SO close! Your order of {cart_total} is fully ready — just needs your final confirmation.\\n\\nAll your details are saved. One click and it is on its way to you: {checkout_url}\\n\\nThis is your easiest checkout ever.",
      ),

      // 7. Payment error condition
      n("7", "condition", "Payment error detected?", 1480, {
        conditionData: [
          {
            variable: "has_payment_error",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Support ticket for payment errors
      zendeskTicketNode(
        "8",
        "Create payment error support ticket",
        1720,
        "Payment Error During Checkout: {customer_name}",
        "A customer experienced a payment error during checkout.\\n\\nCustomer: {customer_name} ({customer_email})\\nCart Total: {cart_total}\\nError Code: {error_code}\\nCheckout Step: {abandonment_step}\\nRecovery URL: {checkout_url}\\n\\nPlease reach out to assist with checkout completion.",
        "high",
        "payment_error,checkout_recovery",
      ),

      // 9. Step recovery logging
      sheetsNode(
        "9",
        "Log step-specific recovery",
        1960,
        "CheckoutStepRecovery",
        '[["{customer_email}","{customer_name}","{abandonment_step}","{cart_total}","{has_payment_error}","{error_code}","{checkout_url}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"), // all paths go through contact email as first touch
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // Payment error -> Zendesk ticket
      e("7", "9", "output_2"), // No error -> skip to logging
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 372. COLLECTION PAGE BROWSE ABANDONMENT (7 nodes)
  // ===================================================================

  "Collection Page Browse Abandonment": {
    summary:
      "Re-engages shoppers who browse a collection page but leave without purchasing. Pulls collection metadata from Shopify, selects standout products, generates AI editorial content, sends a curated collection recap email, tracks click engagement, and logs abandonment data to Sheets.",
    nodes: [
      trigger("Webhook: Collection page exit", "webhook_app", {
        source: "Website Analytics / Shopify Storefront",
        event: "collection.browse.abandoned",
        howItStarts:
          "A customer browses a collection page for a meaningful duration but exits without adding anything to cart or purchasing",
        setup:
          "Connect your analytics platform or storefront events to POST collection browse data (customer email, collection handle, products viewed, time on page, scroll depth) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Pull collection metadata from Shopify
      aiNode(
        "2",
        "AI: Extract browse data",
        280,
        "Extract the customer details and collection browse data from this event. Find the customer email, name, the collection they browsed (handle or ID), products they viewed, time spent, and scroll depth regardless of format. Respond JSON: {customer_email: string, customer_name: string, collection_handle: string, collection_title: string, products_viewed: string, time_on_page_sec: number, scroll_depth_pct: number}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          {
            field: "collection_handle",
            as: "collection_handle",
            type: "string",
          },
          { field: "collection_title", as: "collection_title", type: "string" },
          { field: "products_viewed", as: "products_viewed", type: "string" },
          { field: "time_on_page_sec", as: "time_on_page_sec", type: "number" },
          { field: "scroll_depth_pct", as: "scroll_depth_pct", type: "number" },
        ],
      ),

      // 3. Standout product selection from Shopify collection
      shopifyNode(
        "3",
        "Pull standout products from collection",
        520,
        "GET",
        "collections/{collection_handle}/products.json?limit=5&sort_by=best-selling",
        [],
        [
          {
            name: "products[0].title",
            customName: "star_product_1",
            type: "string",
          },
          {
            name: "products[0].variants[0].price",
            customName: "star_price_1",
            type: "string",
          },
          {
            name: "products[0].images[0].src",
            customName: "star_image_1",
            type: "string",
          },
          {
            name: "products[1].title",
            customName: "star_product_2",
            type: "string",
          },
          {
            name: "products[1].variants[0].price",
            customName: "star_price_2",
            type: "string",
          },
          {
            name: "products[2].title",
            customName: "star_product_3",
            type: "string",
          },
        ],
      ),

      // 4. AI editorial story
      aiNode(
        "4",
        "AI: Write editorial collection story",
        760,
        "You are an editorial e-commerce copywriter. Write a compelling, magazine-style email about this collection. Do not be salesy — tell a story about the collection theme, highlight the standout products naturally, and make the reader curious to explore more. Keep it concise and visually friendly. Respond JSON: {email_subject: string, email_body: string, editorial_hook: string, cta_text: string}",
        "Collection: {collection_title} | Star products: {star_product_1} (${star_price_1}), {star_product_2} (${star_price_2}), {star_product_3} | Customer: {customer_name} | Time browsed: {time_on_page_sec}s | Scroll: {scroll_depth_pct}%",
        [
          { field: "email_subject", as: "editorial_subject", type: "string" },
          { field: "email_body", as: "editorial_body", type: "string" },
          { field: "editorial_hook", as: "editorial_hook", type: "string" },
          { field: "cta_text", as: "cta_text", type: "string" },
        ],
      ),

      // 5. Collection recap email
      emailNode(
        "5",
        "Email: Collection editorial recap",
        1000,
        "{customer_email}",
        "{editorial_subject}",
        "{editorial_body}\\n\\n{editorial_hook}\\n\\nExplore the full collection: https://store.example.com/collections/{collection_handle}\\n\\n{cta_text}",
      ),

      // 6. Click tracking (delay for engagement measurement)
      n("6", "delayAction", "Wait 24h for click tracking", 1240, {
        delayAction: { time: 24, timeFormat: "hours" },
      }),

      // 7. Collection abandonment logging
      sheetsNode(
        "7",
        "Log collection abandonment",
        1480,
        "CollectionBrowseAbandonment",
        '[["{customer_email}","{customer_name}","{collection_title}","{collection_handle}","{time_on_page_sec}","{scroll_depth_pct}","{star_product_1}","{editorial_subject}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 373. COMPLEMENTARY CATEGORY CROSS-SELL (8 nodes)
  // ===================================================================

  "Complementary Category Cross-Sell": {
    summary:
      "Drives incremental revenue by recommending complementary products after purchase. Maps the purchased category to complementary rules, selects products with stock and margin data, generates AI cross-sell copy, waits for optimal timing, sends the cross-sell email, and logs revenue attribution to Sheets.",
    nodes: [
      trigger("Webhook: Purchase completed", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "orders/paid",
        howItStarts:
          "A customer completes a purchase, triggering complementary category analysis based on what they bought",
        setup:
          "Connect your Shopify store or e-commerce platform to POST order completion events (order details, products, customer info, amounts) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Category mapping
      n("2", "runCode", "Map purchased category to complements", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map purchased product category to complementary categories
const payload = JSON.parse(vars.webhook_payload || "{}");
const orderItems = payload.line_items || payload.items || [];
const email = payload.email || payload.customer?.email || "";
const name = payload.customer?.first_name || payload.billing_address?.first_name || "there";
const orderId = payload.id || payload.order_id || "";
const orderTotal = payload.total_price || payload.total || "0";
const primaryCategory = (orderItems[0]?.product_type || orderItems[0]?.category || "general").toLowerCase();
const complementMap = {
  electronics: ["accessories", "cases", "chargers", "warranties"],
  clothing: ["accessories", "shoes", "bags", "jewelry"],
  shoes: ["socks", "shoe-care", "insoles", "laces"],
  beauty: ["skincare", "tools", "fragrance", "sets"],
  home: ["decor", "lighting", "kitchen", "bedding"],
  sports: ["apparel", "nutrition", "accessories", "recovery"],
  kitchen: ["utensils", "storage", "appliances", "cookbooks"],
  furniture: ["decor", "lighting", "rugs", "storage"]
};
const complements = complementMap[primaryCategory] || complementMap.home;
const result = {
  customer_email: email,
  customer_name: name,
  order_id: orderId,
  order_total: orderTotal,
  purchased_category: primaryCategory,
  purchased_product: orderItems[0]?.title || orderItems[0]?.name || "your recent purchase",
  complement_categories: complements.join(", "),
  primary_complement: complements[0],
  secondary_complement: complements[1]
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "order_id", type: "string" },
            { name: "order_total", type: "string" },
            { name: "purchased_category", type: "string" },
            { name: "purchased_product", type: "string" },
            { name: "complement_categories", type: "string" },
            { name: "primary_complement", type: "string" },
            { name: "secondary_complement", type: "string" },
          ],
        },
      }),

      // 3. Pull complementary products with stock/margin from Shopify
      shopifyNode(
        "3",
        "Pull complementary products (Shopify)",
        520,
        "GET",
        "products.json?product_type={primary_complement}&status=active&limit=6",
        [],
        [
          {
            name: "products[0].title",
            customName: "comp_product_1",
            type: "string",
          },
          {
            name: "products[0].variants[0].price",
            customName: "comp_price_1",
            type: "string",
          },
          {
            name: "products[0].variants[0].inventory_quantity",
            customName: "comp_stock_1",
            type: "number",
          },
          {
            name: "products[1].title",
            customName: "comp_product_2",
            type: "string",
          },
          {
            name: "products[1].variants[0].price",
            customName: "comp_price_2",
            type: "string",
          },
          {
            name: "products[2].title",
            customName: "comp_product_3",
            type: "string",
          },
          {
            name: "products[2].variants[0].price",
            customName: "comp_price_3",
            type: "string",
          },
        ],
      ),

      // 4. Product selection with stock/margin filter
      aiNode(
        "4",
        "AI: Write cross-sell copy",
        760,
        "You are a cross-sell copywriter. Write a compelling email recommending complementary products to someone who just bought from a specific category. Make the recommendations feel natural and helpful, not pushy. Explain why these products go perfectly with what they purchased. Respond JSON: {email_subject: string, email_body: string, recommendation_rationale: string, expected_aov_lift: string}",
        "Customer: {customer_name} | Purchased: {purchased_product} (Category: {purchased_category}) | Order: ${order_total} | Complementary: {comp_product_1} (${comp_price_1}), {comp_product_2} (${comp_price_2}), {comp_product_3} (${comp_price_3}) | Stock: {comp_stock_1} units",
        [
          { field: "email_subject", as: "crosssell_subject", type: "string" },
          { field: "email_body", as: "crosssell_body", type: "string" },
          {
            field: "recommendation_rationale",
            as: "recommendation_rationale",
            type: "string",
          },
          {
            field: "expected_aov_lift",
            as: "expected_aov_lift",
            type: "string",
          },
        ],
      ),

      // 5. Timing delay
      n("5", "delayAction", "Wait 3 days post-purchase", 1000, {
        delayAction: { time: 3, timeFormat: "days" },
      }),

      // 6. Cross-sell email
      emailNode(
        "6",
        "Email: Cross-sell recommendations",
        1240,
        "{customer_email}",
        "{crosssell_subject}",
        "{crosssell_body}\\n\\n{recommendation_rationale}\\n\\nShop the full {primary_complement} collection: https://store.example.com/collections/{primary_complement}",
      ),

      // 7. Delay for attribution window
      n("7", "delayAction", "Wait 7 days for attribution", 1480, {
        delayAction: { time: 7, timeFormat: "days" },
      }),

      // 8. Revenue attribution logging
      sheetsNode(
        "8",
        "Log cross-sell attribution",
        1720,
        "CrossSellAttribution",
        '[["{customer_email}","{order_id}","{purchased_category}","{purchased_product}","{order_total}","{primary_complement}","{comp_product_1}","{comp_price_1}","{crosssell_subject}","{expected_aov_lift}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 374. CONCIERGE CART RECOVERY (8 nodes)
  // ===================================================================

  "Concierge Cart Recovery": {
    summary:
      "White-glove cart recovery for high-value abandoned carts. Enriches the customer with LTV and loyalty data from HubSpot, generates an AI concierge-style email, creates a CRM follow-up task, notifies the sales rep on Slack, checks engagement, schedules a personal call for non-responders, and logs concierge ROI.",
    nodes: [
      trigger("Webhook: High-value cart abandoned", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "cart.abandoned.high_value",
        howItStarts:
          "A customer abandons a cart valued above the high-value threshold (e.g., $500+), triggering concierge-level recovery",
        setup:
          "Connect your e-commerce platform to POST high-value cart abandonment events (cart details, customer info, cart total, items) to the workflow URL. Configure the threshold in your platform settings.",
        inputFields: [],
      }),

      // 2. LTV/loyalty enrichment from HubSpot
      aiNode(
        "2",
        "AI: Extract cart + customer data",
        280,
        "Extract customer details, cart contents, and totals from this high-value cart abandonment event. Find the customer email, name, phone, cart total, top items, and checkout URL regardless of payload format. Respond JSON: {customer_email: string, customer_name: string, customer_phone: string, cart_total: number, top_items: string, item_count: number, checkout_url: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "cart_total", as: "cart_total", type: "number" },
          { field: "top_items", as: "top_items", type: "string" },
          { field: "item_count", as: "item_count", type: "number" },
          { field: "checkout_url", as: "checkout_url", type: "string" },
        ],
      ),

      // 3. HubSpot enrichment
      n("3", "hitApi", "Enrich from HubSpot (LTV + loyalty)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","firstname","lastname","hs_object_id","lifecyclestage","total_revenue","num_associated_deals","loyalty_tier"]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.total_revenue",
              customName: "customer_ltv",
              type: "string",
            },
            {
              name: "results[0].properties.loyalty_tier",
              customName: "loyalty_tier",
              type: "string",
            },
            {
              name: "results[0].properties.num_associated_deals",
              customName: "purchase_count",
              type: "string",
            },
            {
              name: "results[0].id",
              customName: "hubspot_contact_id",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI concierge email
      aiNode(
        "4",
        "AI: Write concierge recovery email",
        760,
        "You are a luxury e-commerce concierge. Write a personal, premium-feeling recovery email for a high-value customer who abandoned their cart. Reference their loyalty status and purchase history. Offer personal assistance, not just a discount. Make them feel valued and special. Include a direct line to their personal shopping assistant. Respond JSON: {email_subject: string, email_body: string, personal_touch: string, crm_note: string}",
        "Customer: {customer_name} | Cart: ${cart_total} | Items: {top_items} ({item_count} items) | LTV: ${customer_ltv} | Loyalty: {loyalty_tier} | Purchases: {purchase_count} | Checkout: {checkout_url}",
        [
          { field: "email_subject", as: "concierge_subject", type: "string" },
          { field: "email_body", as: "concierge_body", type: "string" },
          { field: "personal_touch", as: "personal_touch", type: "string" },
          { field: "crm_note", as: "crm_note", type: "string" },
        ],
      ),

      // 5. Send concierge email
      emailNode(
        "5",
        "Email: Concierge cart recovery",
        1000,
        "{customer_email}",
        "{concierge_subject}",
        "{concierge_body}\\n\\n{personal_touch}",
      ),

      // 6. Rep Slack notification
      slackNode(
        "6",
        "Slack: Notify assigned rep",
        1240,
        "*CONCIERGE CART RECOVERY*\\nCustomer: {customer_name} ({customer_email})\\nCart: ${cart_total} | Items: {top_items}\\nLTV: ${customer_ltv} | Tier: {loyalty_tier}\\nPurchase history: {purchase_count} orders\\nCheckout: {checkout_url}\\n\\nConcierge email sent. {crm_note}",
      ),

      // 7. No-engagement condition (check after delay)
      n("7", "condition", "No engagement after 48h?", 1480, {
        conditionData: [
          {
            variable: "email_opened",
            value: "false",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Concierge ROI logging
      sheetsNode(
        "8",
        "Log concierge recovery ROI",
        1720,
        "ConciergeRecovery",
        '[["{customer_email}","{customer_name}","{cart_total}","{customer_ltv}","{loyalty_tier}","{top_items}","{concierge_subject}","{purchase_count}","{hubspot_contact_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // No engagement -> log (rep already notified for call)
      e("7", "8", "output_2"), // Engaged -> log success
    ],
  },

  // ===================================================================
  // 375. COUPON CODE GENERATOR (7 nodes)
  // ===================================================================

  "Coupon Code Generator": {
    summary:
      "Automates coupon code creation with policy validation and approval gates. Validates the request against business rules, generates a unique code, creates it in Shopify, routes high-risk coupons through Slack approval, notifies the requester, and logs to a coupon catalog in Sheets.",
    nodes: [
      trigger("Webhook: Coupon request", "webhook_app", {
        source: "Internal Tool / Slack Command / CRM",
        event: "coupon.request.created",
        howItStarts:
          "A team member requests a new coupon code through an internal tool, Slack command, or CRM action",
        setup:
          "Connect your internal request system or Slack slash command (/coupon) to POST coupon requests (discount type, value, conditions, requester, reason) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Policy validation
      n("2", "runCode", "Validate coupon policy", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate coupon request against business policies
const payload = JSON.parse(vars.webhook_payload || "{}");
const discountType = payload.discount_type || payload.type || "percentage";
const discountValue = parseFloat(payload.discount_value || payload.value || "0");
const minPurchase = parseFloat(payload.minimum_purchase || payload.min_order || "0");
const usageLimit = parseInt(payload.usage_limit || payload.limit || "100");
const expiryDays = parseInt(payload.expiry_days || "30");
const requesterEmail = payload.requester_email || payload.email || "";
const requesterName = payload.requester_name || payload.name || "";
const reason = payload.reason || payload.purpose || "";
const maxDiscount = discountType === "percentage" ? 50 : 100;
const isHighRisk = discountValue > 25 || usageLimit > 500 || (discountType === "fixed" && discountValue > 50);
const isValid = discountValue > 0 && discountValue <= maxDiscount && requesterEmail && reason;
const result = {
  discount_type: discountType,
  discount_value: String(discountValue),
  min_purchase: String(minPurchase),
  usage_limit: String(usageLimit),
  expiry_days: String(expiryDays),
  requester_email: requesterEmail,
  requester_name: requesterName,
  reason: reason,
  is_high_risk: isHighRisk ? "yes" : "no",
  is_valid: isValid ? "yes" : "no",
  policy_notes: isValid ? "Passed all policy checks" : "Failed: check discount value, requester, and reason"
};
return result;`,
          outputVariables: [
            { name: "discount_type", type: "string" },
            { name: "discount_value", type: "string" },
            { name: "min_purchase", type: "string" },
            { name: "usage_limit", type: "string" },
            { name: "expiry_days", type: "string" },
            { name: "requester_email", type: "string" },
            { name: "requester_name", type: "string" },
            { name: "reason", type: "string" },
            { name: "is_high_risk", type: "string" },
            { name: "is_valid", type: "string" },
            { name: "policy_notes", type: "string" },
          ],
        },
      }),

      // 3. Code generation
      n("3", "runCode", "Generate unique coupon code", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a unique, branded coupon code
const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
let code = "OMNI";
for (let i = 0; i < 8; i++) { code += chars[Math.floor(Math.random() * chars.length)]; }
const discountSuffix = vars.discount_type === "percentage" ? vars.discount_value + "PCT" : vars.discount_value + "OFF";
const coupon_code = code + "-" + discountSuffix;
const expiryDate = new Date(Date.now() + parseInt(vars.expiry_days) * 86400000).toISOString().split("T")[0];
const result = {
  coupon_code: coupon_code,
  expiry_date: expiryDate,
  created_at: new Date().toISOString()
};
return result;`,
          outputVariables: [
            { name: "coupon_code", type: "string" },
            { name: "expiry_date", type: "string" },
            { name: "created_at", type: "string" },
          ],
        },
      }),

      // 4. Create coupon in Shopify
      shopifyNode(
        "4",
        "Create coupon in Shopify",
        760,
        "POST",
        "price_rules.json",
        [
          {
            key: "price_rule",
            value:
              '{"title":"{coupon_code}","value_type":"{discount_type}","value":"-{discount_value}","customer_selection":"all","starts_at":"{created_at}","ends_at":"{expiry_date}T23:59:59Z","usage_limit":{usage_limit},"prerequisite_subtotal_range":{"greater_than_or_equal_to":"{min_purchase}"}}',
          },
        ],
        [
          {
            name: "price_rule.id",
            customName: "shopify_rule_id",
            type: "string",
          },
          {
            name: "price_rule.title",
            customName: "shopify_coupon_title",
            type: "string",
          },
        ],
      ),

      // 5. High-risk approval condition
      n("5", "condition", "High-risk coupon (needs approval)?", 1000, {
        conditionData: [
          {
            variable: "is_high_risk",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Requester notification
      emailNode(
        "6",
        "Email: Coupon created notification",
        1240,
        "{requester_email}",
        "Coupon Code Created: {coupon_code}",
        "Hi {requester_name},\\n\\nYour coupon code has been created and is active in Shopify:\\n\\nCode: {coupon_code}\\nDiscount: {discount_value} ({discount_type})\\nMinimum Purchase: ${min_purchase}\\nUsage Limit: {usage_limit}\\nExpires: {expiry_date}\\n\\nReason: {reason}\\nPolicy: {policy_notes}\\n\\nShopify Rule ID: {shopify_rule_id}",
      ),

      // 7. Coupon catalog logging
      sheetsNode(
        "7",
        "Log to coupon catalog",
        1480,
        "CouponCatalog",
        '[["{coupon_code}","{discount_type}","{discount_value}","{min_purchase}","{usage_limit}","{expiry_date}","{requester_name}","{requester_email}","{reason}","{is_high_risk}","{shopify_rule_id}","{created_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // High risk -> still notifies (approval is via Slack review)
      e("5", "6", "output_2"), // Normal -> notify immediately
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 376. CROSS-BORDER CURRENCY CART RECOVERY (8 nodes)
  // ===================================================================

  "Cross-Border Currency Cart Recovery": {
    summary:
      "Recovers international cart abandonments by addressing cross-border friction. Resolves the customer country and currency, localizes pricing with FX rates, estimates duties and taxes, generates AI reassurance copy in their context, sends a localized email, sends a multilingual SMS, and logs market performance data.",
    nodes: [
      trigger("Webhook: International cart abandoned", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "cart.abandoned.international",
        howItStarts:
          "A customer from an international market abandons their cart, identified by non-domestic IP, currency, or shipping address country",
        setup:
          "Connect your e-commerce platform to POST international cart abandonment events (cart details, customer info, detected country, currency, shipping address) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Country/currency resolution
      n("2", "runCode", "Resolve country + currency", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Resolve customer country, currency, and localization params
const payload = JSON.parse(vars.webhook_payload || "{}");
const country = (payload.country_code || payload.shipping_address?.country_code || payload.detected_country || "US").toUpperCase();
const currencyMap = { US: "USD", GB: "GBP", DE: "EUR", FR: "EUR", JP: "JPY", AU: "AUD", CA: "CAD", BR: "BRL", MX: "MXN", IN: "INR", KR: "KRW", CN: "CNY" };
const languageMap = { US: "en", GB: "en", DE: "de", FR: "fr", JP: "ja", AU: "en", CA: "en", BR: "pt", MX: "es", IN: "en", KR: "ko", CN: "zh" };
const dutyRateMap = { US: 0, GB: 0.05, DE: 0.04, FR: 0.04, JP: 0.06, AU: 0.05, CA: 0.03, BR: 0.15, MX: 0.08, IN: 0.10, KR: 0.08, CN: 0.12 };
const email = payload.email || payload.customer?.email || "";
const name = payload.customer_name || payload.customer?.first_name || "there";
const cartTotal = parseFloat(payload.total_price || payload.cart_total || "0");
const checkoutUrl = payload.checkout_url || payload.recovery_url || "";
const items = payload.line_items || payload.items || [];
const topItem = items[0]?.title || items[0]?.name || "your items";
const result = {
  customer_email: email,
  customer_name: name,
  customer_country: country,
  local_currency: currencyMap[country] || "USD",
  language: languageMap[country] || "en",
  cart_total_usd: String(cartTotal),
  duty_rate: String(dutyRateMap[country] || 0.05),
  top_item: topItem,
  item_count: String(items.length || 1),
  checkout_url: checkoutUrl,
  customer_phone: payload.phone || payload.customer?.phone || ""
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_country", type: "string" },
            { name: "local_currency", type: "string" },
            { name: "language", type: "string" },
            { name: "cart_total_usd", type: "string" },
            { name: "duty_rate", type: "string" },
            { name: "top_item", type: "string" },
            { name: "item_count", type: "string" },
            { name: "checkout_url", type: "string" },
            { name: "customer_phone", type: "string" },
          ],
        },
      }),

      // 3. FX localization
      n("3", "hitApi", "Fetch FX rate for local currency", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.exchangerate-api.com/v4/latest/USD",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "rates.{local_currency}",
              customName: "fx_rate",
              type: "number",
            },
            { name: "date", customName: "fx_date", type: "string" },
          ],
        },
      }),

      // 4. Duty/tax estimation
      n("4", "runCode", "Estimate duties + taxes", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate estimated duties, taxes, and localized total
const cartUSD = parseFloat(vars.cart_total_usd);
const fxRate = parseFloat(vars.fx_rate || "1");
const dutyRate = parseFloat(vars.duty_rate || "0.05");
const localCart = (cartUSD * fxRate).toFixed(2);
const estimatedDuty = (cartUSD * dutyRate * fxRate).toFixed(2);
const estimatedVAT = (cartUSD * 0.20 * fxRate).toFixed(2);
const totalLanded = (parseFloat(localCart) + parseFloat(estimatedDuty) + parseFloat(estimatedVAT)).toFixed(2);
const result = {
  local_cart_total: localCart,
  estimated_duty: estimatedDuty,
  estimated_vat: estimatedVAT,
  total_landed_cost: totalLanded,
  currency_symbol: vars.local_currency
};
return result;`,
          outputVariables: [
            { name: "local_cart_total", type: "string" },
            { name: "estimated_duty", type: "string" },
            { name: "estimated_vat", type: "string" },
            { name: "total_landed_cost", type: "string" },
            { name: "currency_symbol", type: "string" },
          ],
        },
      }),

      // 5. AI reassurance copy
      aiNode(
        "5",
        "AI: Write localized reassurance copy",
        1000,
        "You are an international e-commerce copywriter. Write a cart recovery email that addresses cross-border shopping concerns: clear pricing in local currency, duty/tax transparency, reliable international shipping, easy returns, and payment security. Make the customer feel confident buying from abroad. If the language is not English, write the email in that language. Respond JSON: {email_subject: string, email_body: string, sms_text: string, trust_signals: string}",
        "Customer: {customer_name} | Country: {customer_country} | Language: {language} | Item: {top_item} ({item_count} items) | Cart: {local_cart_total} {currency_symbol} | Duty: {estimated_duty} {currency_symbol} | VAT: {estimated_vat} {currency_symbol} | Total landed: {total_landed_cost} {currency_symbol} | Checkout: {checkout_url}",
        [
          { field: "email_subject", as: "localized_subject", type: "string" },
          { field: "email_body", as: "localized_body", type: "string" },
          { field: "sms_text", as: "localized_sms", type: "string" },
          { field: "trust_signals", as: "trust_signals", type: "string" },
        ],
      ),

      // 6. Localized email
      emailNode(
        "6",
        "Email: Localized cart recovery",
        1240,
        "{customer_email}",
        "{localized_subject}",
        "{localized_body}\\n\\n{trust_signals}\\n\\nComplete your order: {checkout_url}",
      ),

      // 7. Multilingual SMS
      smsNode(
        "7",
        "SMS: Multilingual recovery (Twilio)",
        1480,
        "{customer_phone}",
        "{localized_sms}",
      ),

      // 8. Market performance logging
      sheetsNode(
        "8",
        "Log market performance",
        1720,
        "CrossBorderRecovery",
        '[["{customer_email}","{customer_country}","{local_currency}","{cart_total_usd}","{local_cart_total}","{total_landed_cost}","{fx_rate}","{language}","{top_item}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 377. CROSS-CHANNEL BROWSE ABANDONMENT (8 nodes)
  // ===================================================================

  "Cross-Channel Browse Abandonment": {
    summary:
      "Orchestrates browse abandonment recovery across email and SMS channels. Resolves the customer channel preference, sends the primary email, waits to check engagement, conditionally escalates to SMS for non-openers, applies cross-channel suppression, tracks channel attribution, and logs performance data.",
    nodes: [
      trigger("Webhook: Browse abandonment detected", "webhook_app", {
        source: "Website Analytics / CDP",
        event: "browse.abandoned",
        howItStarts:
          "A customer browses products on the website for a significant duration but leaves without adding to cart or purchasing",
        setup:
          "Connect your analytics platform or CDP to POST browse abandonment events (customer email, products viewed, pages visited, session duration, consent flags) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Channel preference resolution
      n("2", "runCode", "Resolve channel preference + consent", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Resolve customer channel preference and consent status
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer_email || "";
const phone = payload.phone || payload.customer_phone || "";
const name = payload.customer_name || payload.name || "there";
const productsViewed = payload.products_viewed || payload.viewed_products || [];
const topProduct = Array.isArray(productsViewed) ? (productsViewed[0]?.title || productsViewed[0] || "") : productsViewed;
const sessionDuration = payload.session_duration || payload.time_on_site || 0;
const emailConsent = payload.email_consent !== false && email;
const smsConsent = (payload.sms_consent === true || payload.sms_consent === "true") && phone;
const preferredChannel = smsConsent && !emailConsent ? "sms" : "email";
const productUrl = payload.product_url || payload.viewed_url || "https://store.example.com";
const result = {
  customer_email: email,
  customer_phone: phone,
  customer_name: name,
  top_product: typeof topProduct === "object" ? topProduct.title || JSON.stringify(topProduct) : topProduct,
  product_url: productUrl,
  session_duration_sec: String(sessionDuration),
  email_consent: emailConsent ? "yes" : "no",
  sms_consent: smsConsent ? "yes" : "no",
  preferred_channel: preferredChannel,
  products_viewed_count: String(Array.isArray(productsViewed) ? productsViewed.length : 1)
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "top_product", type: "string" },
            { name: "product_url", type: "string" },
            { name: "session_duration_sec", type: "string" },
            { name: "email_consent", type: "string" },
            { name: "sms_consent", type: "string" },
            { name: "preferred_channel", type: "string" },
            { name: "products_viewed_count", type: "string" },
          ],
        },
      }),

      // 3. Email primary send
      emailNode(
        "3",
        "Email: Browse abandonment reminder",
        520,
        "{customer_email}",
        "Still thinking about {top_product}?",
        "Hi {customer_name},\\n\\nWe noticed you were checking out {top_product} and a few other items. Great taste!\\n\\nHere is a quick link back to where you left off: {product_url}\\n\\nBrowsing {products_viewed_count} products in {session_duration_sec} seconds tells us you found something interesting. Do not let it slip away.\\n\\nHappy shopping!",
      ),

      // 4. Open/click delay check
      n("4", "delayAction", "Wait 4 hours for engagement", 760, {
        delayAction: { time: 4, timeFormat: "hours" },
      }),

      // 5. SMS escalation condition
      n("5", "condition", "No email open + SMS consent?", 1000, {
        conditionData: [
          {
            variable: "sms_consent",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. SMS escalation
      smsNode(
        "6",
        "SMS: Browse reminder (Twilio)",
        1240,
        "{customer_phone}",
        "Hi {customer_name}! Still interested in {top_product}? Check it out: {product_url}",
      ),

      // 7. Channel attribution delay
      n("7", "delayAction", "Wait 48h for attribution", 1480, {
        delayAction: { time: 48, timeFormat: "hours" },
      }),

      // 8. Performance logging
      sheetsNode(
        "8",
        "Log cross-channel performance",
        1720,
        "CrossChannelBrowseAbandonment",
        '[["{customer_email}","{customer_name}","{top_product}","{products_viewed_count}","{session_duration_sec}","{preferred_channel}","{email_consent}","{sms_consent}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // SMS consent -> escalate to SMS
      e("5", "7", "output_2"), // No SMS consent -> skip to attribution
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 378. CROSS-CHANNEL CART ABANDONMENT (9 nodes)
  // ===================================================================

  "Cross-Channel Cart Abandonment": {
    summary:
      "Multi-touch cart recovery across email and SMS with smart suppression. Checks channel consent, sends email with full cart details, waits for engagement, conditionally escalates to SMS, falls back to a second email, applies purchase suppression, computes multi-touch attribution, and logs the full sequence.",
    nodes: [
      trigger("Webhook: Cart abandoned", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "carts/update",
        howItStarts:
          "A customer adds items to cart but does not proceed to checkout or complete purchase within the abandonment window",
        setup:
          "Connect your Shopify store or e-commerce platform to POST cart abandonment events (cart ID, items, totals, customer info, consent flags) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Channel + consent check
      n("2", "runCode", "Check channels + consent", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse cart abandonment and check channel consent
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer?.email || "";
const phone = payload.phone || payload.customer?.phone || "";
const name = payload.customer_name || payload.customer?.first_name || "there";
const cartItems = payload.line_items || payload.items || [];
const cartTotal = payload.total_price || payload.total || "0";
const checkoutUrl = payload.checkout_url || payload.abandoned_checkout_url || "";
const itemSummary = cartItems.map(i => i.title || i.name).slice(0, 3).join(", ");
const topItem = cartItems[0]?.title || cartItems[0]?.name || "your items";
const emailConsent = payload.email_marketing_consent !== false && !!email;
const smsConsent = (payload.sms_marketing_consent === true || payload.sms_consent === "true") && !!phone;
const result = {
  customer_email: email,
  customer_phone: phone,
  customer_name: name,
  cart_total: cartTotal,
  top_item: topItem,
  item_summary: itemSummary,
  item_count: String(cartItems.length || 1),
  checkout_url: checkoutUrl,
  email_consent: emailConsent ? "yes" : "no",
  sms_consent: smsConsent ? "yes" : "no",
  cart_id: payload.cart_id || payload.id || ""
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "cart_total", type: "string" },
            { name: "top_item", type: "string" },
            { name: "item_summary", type: "string" },
            { name: "item_count", type: "string" },
            { name: "checkout_url", type: "string" },
            { name: "email_consent", type: "string" },
            { name: "sms_consent", type: "string" },
            { name: "cart_id", type: "string" },
          ],
        },
      }),

      // 3. Email with full cart
      emailNode(
        "3",
        "Email 1: Full cart recovery",
        520,
        "{customer_email}",
        "You left {item_count} item(s) in your cart!",
        "Hi {customer_name},\\n\\nYou left these behind:\\n{item_summary}\\n\\nTotal: {cart_total}\\n\\nYour cart is saved and ready: {checkout_url}\\n\\nNeed help? Just reply to this email.",
      ),

      // 4. Wait + engagement check
      n("4", "delayAction", "Wait 4 hours", 760, {
        delayAction: { time: 4, timeFormat: "hours" },
      }),

      // 5. SMS escalation condition
      n("5", "condition", "SMS consent available?", 1000, {
        conditionData: [
          {
            variable: "sms_consent",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. SMS escalation
      smsNode(
        "6",
        "SMS: Cart reminder (Twilio)",
        1240,
        "{customer_phone}",
        "Hi {customer_name}, your {top_item} + {item_count} items ({cart_total}) are still waiting! Complete your order: {checkout_url}",
      ),

      // 7. Second email fallback
      emailNode(
        "7",
        "Email 2: Urgency follow-up",
        1480,
        "{customer_email}",
        "Last chance: {top_item} is going fast!",
        "Hi {customer_name},\\n\\nJust a quick note — your {top_item} is popular and stock is moving fast.\\n\\nYour cart ({cart_total}): {checkout_url}\\n\\nWe would hate for you to miss out!",
      ),

      // 8. Multi-touch attribution delay
      n("8", "delayAction", "Wait 72h for attribution", 1720, {
        delayAction: { time: 72, timeFormat: "hours" },
      }),

      // 9. Sequence logging
      sheetsNode(
        "9",
        "Log multi-channel sequence",
        1960,
        "CrossChannelCartRecovery",
        '[["{customer_email}","{cart_id}","{cart_total}","{item_count}","{item_summary}","{email_consent}","{sms_consent}","{checkout_url}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // SMS consent -> SMS escalation
      e("5", "7", "output_2"), // No SMS -> second email
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 379. CROSS-SELL AFTER FIRST PURCHASE (8 nodes)
  // ===================================================================

  "Cross-Sell After First Purchase": {
    summary:
      "Capitalizes on first-purchase momentum to recommend complementary products. Maps the purchase to complementary categories, generates AI-personalized recommendations, enriches product data from Shopify, waits for delivery timing, sends a cross-sell email, tags the customer in HubSpot for affinity tracking, and logs revenue data.",
    nodes: [
      trigger("Webhook: First purchase completed", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "orders/paid",
        howItStarts:
          "A customer completes their very first purchase, identified by order count = 1 in the event payload",
        setup:
          "Connect your e-commerce platform to POST first-order events (order details, customer info, order count, products, totals) to the workflow URL. Filter for order_count = 1 in your webhook settings.",
        inputFields: [],
      }),

      // 2. Complementary mapping
      n("2", "runCode", "Map purchase to cross-sell targets", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map first purchase to cross-sell targets
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer?.email || "";
const name = payload.customer?.first_name || payload.billing_address?.first_name || "there";
const orderId = payload.id || payload.order_id || "";
const orderTotal = payload.total_price || "0";
const items = payload.line_items || [];
const purchasedProduct = items[0]?.title || items[0]?.name || "your purchase";
const productType = (items[0]?.product_type || items[0]?.category || "general").toLowerCase();
const crossSellMap = {
  electronics: { categories: ["accessories", "warranties", "cables"], timing_days: 3 },
  clothing: { categories: ["accessories", "shoes", "care-products"], timing_days: 5 },
  shoes: { categories: ["socks", "care-kits", "insoles"], timing_days: 5 },
  beauty: { categories: ["tools", "travel-sets", "refills"], timing_days: 7 },
  home: { categories: ["decor", "organizers", "cleaning"], timing_days: 7 },
  food: { categories: ["kitchen-tools", "storage", "subscriptions"], timing_days: 3 },
  sports: { categories: ["nutrition", "accessories", "apparel"], timing_days: 5 }
};
const mapping = crossSellMap[productType] || { categories: ["bestsellers", "new-arrivals"], timing_days: 5 };
const result = {
  customer_email: email,
  customer_name: name,
  order_id: orderId,
  order_total: orderTotal,
  purchased_product: purchasedProduct,
  product_type: productType,
  cross_sell_category_1: mapping.categories[0],
  cross_sell_category_2: mapping.categories[1],
  cross_sell_category_3: mapping.categories[2] || mapping.categories[0],
  delivery_delay_days: String(mapping.timing_days)
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "order_id", type: "string" },
            { name: "order_total", type: "string" },
            { name: "purchased_product", type: "string" },
            { name: "product_type", type: "string" },
            { name: "cross_sell_category_1", type: "string" },
            { name: "cross_sell_category_2", type: "string" },
            { name: "cross_sell_category_3", type: "string" },
            { name: "delivery_delay_days", type: "string" },
          ],
        },
      }),

      // 3. AI personalized recommendations
      aiNode(
        "3",
        "AI: Generate personalized cross-sell",
        520,
        "You are a first-purchase cross-sell specialist. Write a warm, congratulatory email for a customer who just made their first purchase. Recommend complementary products that enhance their purchase. Be celebratory about their first order, helpful with recommendations, and not too salesy. Respond JSON: {email_subject: string, email_body: string, recommendation_1: string, recommendation_2: string, recommendation_3: string}",
        "Customer: {customer_name} (first purchase!) | Bought: {purchased_product} ({product_type}) | Spent: ${order_total} | Cross-sell categories: {cross_sell_category_1}, {cross_sell_category_2}, {cross_sell_category_3}",
        [
          { field: "email_subject", as: "crosssell_subject", type: "string" },
          { field: "email_body", as: "crosssell_body", type: "string" },
          { field: "recommendation_1", as: "rec_1", type: "string" },
          { field: "recommendation_2", as: "rec_2", type: "string" },
          { field: "recommendation_3", as: "rec_3", type: "string" },
        ],
      ),

      // 4. Enrichment with price/reviews from Shopify
      shopifyNode(
        "4",
        "Pull cross-sell products (Shopify)",
        760,
        "GET",
        "products.json?product_type={cross_sell_category_1}&status=active&limit=3",
        [],
        [
          {
            name: "products[0].title",
            customName: "shop_rec_1",
            type: "string",
          },
          {
            name: "products[0].variants[0].price",
            customName: "shop_price_1",
            type: "string",
          },
          {
            name: "products[0].handle",
            customName: "shop_handle_1",
            type: "string",
          },
          {
            name: "products[1].title",
            customName: "shop_rec_2",
            type: "string",
          },
          {
            name: "products[1].variants[0].price",
            customName: "shop_price_2",
            type: "string",
          },
        ],
      ),

      // 5. Delivery timing delay
      n("5", "delayAction", "Wait for delivery timing", 1000, {
        delayAction: { time: 5, timeFormat: "days" },
      }),

      // 6. Cross-sell email
      emailNode(
        "6",
        "Email: First-purchase cross-sell",
        1240,
        "{customer_email}",
        "{crosssell_subject}",
        "{crosssell_body}\\n\\nRecommended for you:\\n- {shop_rec_1} — ${shop_price_1}\\n- {shop_rec_2} — ${shop_price_2}\\n\\nShop now: https://store.example.com/collections/{cross_sell_category_1}",
      ),

      // 7. Affinity tagging in HubSpot
      hubspotUpdateNode(
        "7",
        "Tag affinity in HubSpot",
        1480,
        "{customer_email}",
        `{"product_affinity":"{product_type}","first_purchase_date":"${new Date().toISOString().split("T")[0]}","cross_sell_sent":"true","first_order_value":"{order_total}"}`,
        [{ name: "id", customName: "hubspot_updated_id", type: "string" }],
      ),

      // 8. Revenue logging
      sheetsNode(
        "8",
        "Log cross-sell revenue data",
        1720,
        "FirstPurchaseCrossSell",
        '[["{customer_email}","{customer_name}","{order_id}","{purchased_product}","{product_type}","{order_total}","{cross_sell_category_1}","{shop_rec_1}","{shop_price_1}","{crosssell_subject}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 380. CURBSIDE PICKUP COORDINATOR BOT (8 nodes)
  // ===================================================================

  "Curbside Pickup Coordinator Bot": {
    summary:
      "Manages curbside pickup logistics from customer arrival to handoff. Pulls order and store data from Shopify, uses an AI conversational assistant for status updates, checks pack queue readiness, alerts staff on Slack when the customer arrives, estimates wait time, conditionally escalates to a human for complex cases, and logs pickup data.",
    nodes: [
      trigger("Webhook: Curbside pickup request", "webhook_app", {
        source: "SMS / Chat Widget / Mobile App",
        event: "curbside.arrival",
        howItStarts:
          "A customer sends an SMS, chat message, or taps the app button to indicate they have arrived for curbside pickup",
        setup:
          "Connect your SMS provider (Twilio), chat widget, or mobile app to POST curbside arrival messages (customer phone/ID, order number, arrival details) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Order + store pull from Shopify
      aiNode(
        "2",
        "AI: Extract order + customer info",
        280,
        "Extract the order number, customer name, phone number, and any arrival details from this curbside pickup message. The message may be informal (e.g., 'I am here for order 12345' or 'here to pick up, name is John'). Find the information regardless of format. Respond JSON: {order_number: string, customer_name: string, customer_phone: string, arrival_note: string, vehicle_description: string}",
        "{webhook_payload}",
        [
          { field: "order_number", as: "order_number", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "arrival_note", as: "arrival_note", type: "string" },
          {
            field: "vehicle_description",
            as: "vehicle_description",
            type: "string",
          },
        ],
      ),

      // 3. Shopify order lookup
      shopifyNode(
        "3",
        "Pull order from Shopify",
        520,
        "GET",
        "orders.json?name={order_number}&status=any&limit=1",
        [],
        [
          {
            name: "orders[0].id",
            customName: "shopify_order_id",
            type: "string",
          },
          {
            name: "orders[0].fulfillment_status",
            customName: "fulfillment_status",
            type: "string",
          },
          {
            name: "orders[0].line_items[0].title",
            customName: "order_item_summary",
            type: "string",
          },
          {
            name: "orders[0].total_price",
            customName: "order_total",
            type: "string",
          },
          { name: "orders[0].note", customName: "order_notes", type: "string" },
        ],
      ),

      // 4. AI conversational status update
      aiNode(
        "4",
        "AI: Generate pickup status response",
        760,
        "You are a friendly curbside pickup assistant. Based on the order status and details, generate a conversational response for the customer. If the order is ready, confirm and give an ETA. If still being prepared, set expectations warmly. Include the order summary. Also generate an internal staff message. Respond JSON: {customer_response: string, staff_message: string, is_order_ready: string, estimated_wait_minutes: number}",
        "Order: {order_number} | Status: {fulfillment_status} | Items: {order_item_summary} | Total: ${order_total} | Customer: {customer_name} | Vehicle: {vehicle_description} | Notes: {order_notes}",
        [
          {
            field: "customer_response",
            as: "customer_response",
            type: "string",
          },
          { field: "staff_message", as: "staff_message", type: "string" },
          { field: "is_order_ready", as: "is_order_ready", type: "string" },
          {
            field: "estimated_wait_minutes",
            as: "estimated_wait_minutes",
            type: "number",
          },
        ],
      ),

      // 5. Customer arrival Slack alert
      slackNode(
        "5",
        "Slack: Customer arrival alert",
        1000,
        "*CURBSIDE PICKUP — CUSTOMER ARRIVED*\\nCustomer: {customer_name} ({customer_phone})\\nOrder: {order_number} | Total: ${order_total}\\nItems: {order_item_summary}\\nVehicle: {vehicle_description}\\nStatus: {fulfillment_status}\\nEst. wait: {estimated_wait_minutes} min\\n\\n{staff_message}",
      ),

      // 6. Wait time estimation
      n("6", "runCode", "Calculate wait time + priority", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate actual wait time and priority level
const isReady = vars.is_order_ready === "yes" || vars.is_order_ready === "true";
const baseWait = parseInt(vars.estimated_wait_minutes || "5");
const orderTotal = parseFloat(vars.order_total || "0");
const priority = orderTotal > 100 ? "high" : isReady ? "normal" : "low";
const adjustedWait = isReady ? Math.min(baseWait, 5) : baseWait;
const result = {
  final_wait_minutes: String(adjustedWait),
  priority_level: priority,
  ready_for_handoff: isReady ? "yes" : "no",
  handoff_instructions: isReady ? "Order is packed and ready. Proceed to handoff bay." : "Order still being prepared. Alert customer when ready."
};
return result;`,
          outputVariables: [
            { name: "final_wait_minutes", type: "string" },
            { name: "priority_level", type: "string" },
            { name: "ready_for_handoff", type: "string" },
            { name: "handoff_instructions", type: "string" },
          ],
        },
      }),

      // 7. Human escalation condition
      n("7", "condition", "Needs human escalation?", 1480, {
        conditionData: [
          {
            variable: "ready_for_handoff",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Pickup logging
      sheetsNode(
        "8",
        "Log pickup interaction",
        1720,
        "CurbsidePickups",
        '[["{customer_name}","{customer_phone}","{order_number}","{order_total}","{fulfillment_status}","{final_wait_minutes}","{priority_level}","{ready_for_handoff}","{vehicle_description}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // Not ready -> log (staff already notified via Slack)
      e("7", "8", "output_2"), // Ready -> log
    ],
  },

  // ===================================================================
  // 381. CUSTOMER WISHLIST REMINDER (8 nodes)
  // ===================================================================

  "Customer Wishlist Reminder": {
    summary:
      "Automated wishlist monitoring that alerts customers to price drops, low stock, and restocks. Scans wishlist items on a schedule, detects price and stock changes, generates AI copy tailored to the trigger type, sends email and SMS alerts, tracks conversion from trigger to purchase, and logs cadence tuning data.",
    nodes: [
      trigger("Scheduled: Wishlist scan", "schedule", {
        schedule: "Every 4 hours during business hours",
        howItStarts:
          "A scheduled job runs every 4 hours to scan customer wishlists for price drops, low stock alerts, and restocked items",
        setup:
          "Configure the schedule to run every 4 hours. Ensure your wishlist data is accessible via API (Shopify metafields, custom wishlist service, or third-party wishlist app).",
        inputFields: [],
      }),

      // 2. Wishlist items pull
      n("2", "hitApi", "Pull wishlist items with changes", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/products.json?ids={wishlist_product_ids}&fields=id,title,variants,status",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "products[0].title",
              customName: "product_title",
              type: "string",
            },
            {
              name: "products[0].variants[0].price",
              customName: "current_price",
              type: "string",
            },
            {
              name: "products[0].variants[0].compare_at_price",
              customName: "original_price",
              type: "string",
            },
            {
              name: "products[0].variants[0].inventory_quantity",
              customName: "stock_level",
              type: "number",
            },
            {
              name: "products[0].handle",
              customName: "product_handle",
              type: "string",
            },
            {
              name: "products.length",
              customName: "products_checked",
              type: "number",
            },
          ],
        },
      }),

      // 3. Price/stock change detection
      n("3", "runCode", "Detect price and stock changes", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Detect meaningful price drops, low stock, or restocks
const currentPrice = parseFloat(vars.current_price || "0");
const originalPrice = parseFloat(vars.original_price || vars.current_price || "0");
const stockLevel = parseInt(vars.stock_level || "0");
const priceDrop = originalPrice > 0 ? ((originalPrice - currentPrice) / originalPrice * 100).toFixed(1) : "0";
const hasPriceDrop = parseFloat(priceDrop) >= 5;
const isLowStock = stockLevel > 0 && stockLevel <= 5;
const isBackInStock = stockLevel > 0; // was zero, now available
const triggerType = hasPriceDrop ? "price_drop" : isLowStock ? "low_stock" : "general_reminder";
const urgencyLevel = hasPriceDrop && isLowStock ? "critical" : hasPriceDrop ? "high" : isLowStock ? "high" : "normal";
const result = {
  price_drop_pct: priceDrop,
  has_price_drop: hasPriceDrop ? "yes" : "no",
  is_low_stock: isLowStock ? "yes" : "no",
  trigger_type: triggerType,
  urgency_level: urgencyLevel,
  savings_amount: (originalPrice - currentPrice).toFixed(2),
  customer_email: vars.customer_email || "",
  customer_name: vars.customer_name || "there",
  customer_phone: vars.customer_phone || ""
};
return result;`,
          outputVariables: [
            { name: "price_drop_pct", type: "string" },
            { name: "has_price_drop", type: "string" },
            { name: "is_low_stock", type: "string" },
            { name: "trigger_type", type: "string" },
            { name: "urgency_level", type: "string" },
            { name: "savings_amount", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
          ],
        },
      }),

      // 4. AI reminder copy
      aiNode(
        "4",
        "AI: Write wishlist alert copy",
        760,
        "You are a wishlist notification copywriter. Based on the trigger type (price_drop, low_stock, or general_reminder), write a compelling, personalized email. For price drops, emphasize savings. For low stock, create urgency. For general reminders, reignite interest. Also write a short SMS for major price drops. Respond JSON: {email_subject: string, email_body: string, sms_text: string, cta_text: string}",
        "Product: {product_title} | Price: ${current_price} (was ${original_price}) | Drop: {price_drop_pct}% | Savings: ${savings_amount} | Stock: {stock_level} units | Trigger: {trigger_type} | Urgency: {urgency_level} | Customer: {customer_name}",
        [
          { field: "email_subject", as: "wishlist_subject", type: "string" },
          { field: "email_body", as: "wishlist_body", type: "string" },
          { field: "sms_text", as: "wishlist_sms", type: "string" },
          { field: "cta_text", as: "wishlist_cta", type: "string" },
        ],
      ),

      // 5. Wishlist email
      emailNode(
        "5",
        "Email: Wishlist alert",
        1000,
        "{customer_email}",
        "{wishlist_subject}",
        "{wishlist_body}\\n\\n{wishlist_cta}\\n\\nView product: https://store.example.com/products/{product_handle}",
      ),

      // 6. SMS for major drops
      smsNode(
        "6",
        "SMS: Major price drop alert (Twilio)",
        1240,
        "{customer_phone}",
        "{wishlist_sms}",
      ),

      // 7. Trigger-to-purchase tracking delay
      n("7", "delayAction", "Wait 72h for conversion tracking", 1480, {
        delayAction: { time: 72, timeFormat: "hours" },
      }),

      // 8. Cadence tuning logging
      sheetsNode(
        "8",
        "Log wishlist alert cadence",
        1720,
        "WishlistAlerts",
        '[["{customer_email}","{product_title}","{trigger_type}","{price_drop_pct}","{savings_amount}","{stock_level}","{urgency_level}","{current_price}","{original_price}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 382. DELIVERED CONFIRMATION (7 nodes)
  // ===================================================================

  "Delivered Confirmation": {
    summary:
      "Sends a polished delivery confirmation when a package arrives. Pulls order and carrier details from Shopify, generates an AI confirmation message, sends the confirmation email with support links, updates the CRM delivery status in HubSpot, and logs delivery timing data to Sheets.",
    nodes: [
      trigger("Webhook: Package delivered", "webhook_app", {
        source: "Shipping Carrier / Shopify / AfterShip",
        event: "fulfillment.delivered",
        howItStarts:
          "The shipping carrier or tracking service confirms that a package has been delivered to the customer",
        setup:
          "Connect your shipping provider (AfterShip, ShipStation, Shopify fulfillment webhooks) to POST delivery confirmation events (tracking number, order ID, delivery timestamp, carrier) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Order/carrier pull from Shopify
      aiNode(
        "2",
        "AI: Extract delivery details",
        280,
        "Extract the order number, tracking number, carrier name, delivery timestamp, and customer details from this delivery confirmation event. Find these fields regardless of payload format. Respond JSON: {order_number: string, tracking_number: string, carrier: string, delivered_at: string, customer_email: string, customer_name: string, delivery_address: string}",
        "{webhook_payload}",
        [
          { field: "order_number", as: "order_number", type: "string" },
          { field: "tracking_number", as: "tracking_number", type: "string" },
          { field: "carrier", as: "carrier", type: "string" },
          { field: "delivered_at", as: "delivered_at", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "delivery_address", as: "delivery_address", type: "string" },
        ],
      ),

      // 3. Shopify order details
      shopifyNode(
        "3",
        "Pull order details (Shopify)",
        520,
        "GET",
        "orders.json?name={order_number}&status=any&limit=1",
        [],
        [
          {
            name: "orders[0].line_items[0].title",
            customName: "order_items",
            type: "string",
          },
          {
            name: "orders[0].total_price",
            customName: "order_total",
            type: "string",
          },
          {
            name: "orders[0].created_at",
            customName: "order_date",
            type: "string",
          },
          {
            name: "orders[0].id",
            customName: "shopify_order_id",
            type: "string",
          },
        ],
      ),

      // 4. AI confirmation message
      aiNode(
        "4",
        "AI: Write delivery confirmation",
        760,
        "Write a warm, helpful delivery confirmation email. Congratulate the customer on receiving their order, provide a summary of what was delivered, include next steps (how to reach support, return policy link, leave a review), and set expectations for any follow-up. Keep it concise and positive. Respond JSON: {email_subject: string, email_body: string, next_steps: string}",
        "Customer: {customer_name} | Order: {order_number} | Items: {order_items} | Total: ${order_total} | Carrier: {carrier} | Tracking: {tracking_number} | Delivered: {delivered_at} | Address: {delivery_address}",
        [
          {
            field: "email_subject",
            as: "confirmation_subject",
            type: "string",
          },
          { field: "email_body", as: "confirmation_body", type: "string" },
          { field: "next_steps", as: "next_steps", type: "string" },
        ],
      ),

      // 5. Confirmation email
      emailNode(
        "5",
        "Email: Delivery confirmation",
        1000,
        "{customer_email}",
        "{confirmation_subject}",
        "{confirmation_body}\\n\\n{next_steps}\\n\\nNeed help? Contact us: support@store.example.com\\nReturn policy: https://store.example.com/returns",
      ),

      // 6. CRM delivery update in HubSpot
      n("6", "hitApi", "Update delivery status in HubSpot", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
            },
            {
              key: "properties",
              value: '["email","last_delivery_date","total_deliveries"]',
            },
          ],
          variables: [
            {
              name: "results[0].id",
              customName: "hubspot_contact_id",
              type: "string",
            },
            { name: "total", customName: "crm_match_count", type: "number" },
          ],
        },
      }),

      // 7. Delivery timing logging
      sheetsNode(
        "7",
        "Log delivery timing data",
        1480,
        "DeliveryConfirmations",
        '[["{customer_email}","{customer_name}","{order_number}","{order_total}","{carrier}","{tracking_number}","{delivered_at}","{order_date}","{order_items}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 383. DELIVERY-TO-NPS SURVEY (8 nodes)
  // ===================================================================

  "Delivery-to-NPS Survey": {
    summary:
      "Collects NPS feedback after delivery with smart routing by response. Waits an experience period after delivery, sends an NPS survey email, captures the response, routes by promoter/passive/detractor score, flags advocacy candidates, creates support tickets for detractors, and logs NPS trend data.",
    nodes: [
      trigger("Webhook: Package delivered", "webhook_app", {
        source: "Shipping Carrier / Shopify / AfterShip",
        event: "fulfillment.delivered",
        howItStarts:
          "A delivery confirmation triggers the NPS survey countdown — the survey is sent after the customer has had time to experience the product",
        setup:
          "Connect your shipping or fulfillment system to POST delivery events (order ID, customer info, delivery date, items) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Experience delay
      aiNode(
        "2",
        "AI: Extract delivery info for NPS",
        280,
        "Extract the customer email, name, order number, products delivered, and order total from this delivery event. These will be used for the NPS survey. Respond JSON: {customer_email: string, customer_name: string, order_number: string, products_delivered: string, order_total: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          {
            field: "products_delivered",
            as: "products_delivered",
            type: "string",
          },
          { field: "order_total", as: "order_total", type: "string" },
        ],
      ),

      // 3. Experience delay (let customer use product)
      n("3", "delayAction", "Wait 5 days for product experience", 520, {
        delayAction: { time: 5, timeFormat: "days" },
      }),

      // 4. NPS survey email
      emailNode(
        "4",
        "Email: NPS survey request",
        760,
        "{customer_email}",
        "How was your experience, {customer_name}?",
        "Hi {customer_name},\\n\\nYou received your order ({order_number}) a few days ago — {products_delivered}.\\n\\nOn a scale of 0-10, how likely are you to recommend us to a friend?\\n\\nClick your score:\\n0: https://store.example.com/nps?score=0&order={order_number}\\n1-3: https://store.example.com/nps?score=3&order={order_number}\\n4-6: https://store.example.com/nps?score=5&order={order_number}\\n7-8: https://store.example.com/nps?score=8&order={order_number}\\n9-10: https://store.example.com/nps?score=10&order={order_number}\\n\\nYour feedback helps us improve. Thank you!",
      ),

      // 5. Response capture delay
      n("5", "delayAction", "Wait 48h for NPS response", 1000, {
        delayAction: { time: 48, timeFormat: "hours" },
      }),

      // 6. Promoter/passive/detractor routing
      n("6", "condition", "Promoter (score >= 9)?", 1240, {
        conditionData: [
          {
            variable: "nps_score",
            value: "9",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Advocacy candidate flagging / Detractor support ticket
      zendeskTicketNode(
        "7",
        "Create detractor support ticket",
        1480,
        "NPS Detractor Alert: {customer_name} (Order {order_number})",
        "A customer gave a low NPS score after delivery.\\n\\nCustomer: {customer_name} ({customer_email})\\nOrder: {order_number}\\nProducts: {products_delivered}\\nNPS Score: {nps_score}\\nFeedback: {nps_feedback}\\n\\nPlease reach out within 24 hours to understand and resolve their concerns.",
        "high",
        "nps_detractor,delivery_feedback",
      ),

      // 8. NPS trend logging
      sheetsNode(
        "8",
        "Log NPS trend data",
        1720,
        "NPSTrends",
        '[["{customer_email}","{customer_name}","{order_number}","{products_delivered}","{order_total}","{nps_score}","{nps_feedback}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"), // Promoter -> log (advocacy candidate)
      e("6", "7", "output_2"), // Passive/Detractor -> support ticket
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 384. DELIVERY-TO-REVIEW REQUEST (7 nodes)
  // ===================================================================

  "Delivery-to-Review Request": {
    summary:
      "Solicits product reviews at the optimal post-delivery moment. Waits for the customer to experience the product, checks if they already left a review, generates an AI-personalized review request, sends the request email, follows up via SMS, and logs review attribution data.",
    nodes: [
      trigger("Webhook: Package delivered", "webhook_app", {
        source: "Shipping Carrier / Shopify / AfterShip",
        event: "fulfillment.delivered",
        howItStarts:
          "A delivery confirmation triggers a timed review request — the email is sent after the customer has had time to use the product",
        setup:
          "Connect your shipping or fulfillment system to POST delivery events (order ID, customer info, products, delivery date) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Review delay
      aiNode(
        "2",
        "AI: Extract delivery data for review",
        280,
        "Extract the customer email, name, phone, order number, primary product purchased, and product handle/URL from this delivery event. These will be used for review solicitation. Respond JSON: {customer_email: string, customer_name: string, customer_phone: string, order_number: string, product_name: string, product_handle: string, product_id: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_handle", as: "product_handle", type: "string" },
          { field: "product_id", as: "product_id", type: "string" },
        ],
      ),

      // 3. Existing review check condition + delay
      n("3", "condition", "Review delay period passed?", 520, {
        conditionData: [
          {
            variable: "existing_review",
            value: "false",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI personalized review request
      aiNode(
        "4",
        "AI: Write personalized review request",
        760,
        "You are a review request copywriter. Write a warm, personal email asking the customer to leave a review for their specific product. Make it feel genuine, not automated. Reference the product by name, acknowledge their purchase, and explain how their review helps other shoppers. Keep it short. Also write a brief SMS follow-up. Respond JSON: {email_subject: string, email_body: string, sms_text: string, review_url: string}",
        "Customer: {customer_name} | Product: {product_name} | Order: {order_number} | Product page: https://store.example.com/products/{product_handle}",
        [
          { field: "email_subject", as: "review_subject", type: "string" },
          { field: "email_body", as: "review_body", type: "string" },
          { field: "sms_text", as: "review_sms", type: "string" },
          { field: "review_url", as: "review_url", type: "string" },
        ],
      ),

      // 5. Review request email
      emailNode(
        "5",
        "Email: Review request",
        1000,
        "{customer_email}",
        "{review_subject}",
        "{review_body}\\n\\nLeave your review: https://store.example.com/products/{product_handle}#reviews",
      ),

      // 6. SMS reminder
      smsNode(
        "6",
        "SMS: Review reminder (Twilio)",
        1240,
        "{customer_phone}",
        "{review_sms}",
      ),

      // 7. Review attribution logging
      sheetsNode(
        "7",
        "Log review request attribution",
        1480,
        "ReviewRequests",
        '[["{customer_email}","{customer_name}","{order_number}","{product_name}","{product_handle}","{review_subject}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // No existing review -> send request
      e("3", "7", "output_2"), // Already reviewed -> skip to logging
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 385. DISCOUNT ON SECOND TOUCH ONLY (8 nodes)
  // ===================================================================

  "Discount on Second Touch Only": {
    summary:
      "Withholds discounts from the first cart recovery email to preserve margin, only offering an incentive on the second touch. Sends a no-discount reminder first, waits for engagement, checks conversion, generates a unique discount code for non-converters, sends the discount email, tracks redemption, and logs margin savings.",
    nodes: [
      trigger("Webhook: Cart abandoned", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "checkouts/update",
        howItStarts:
          "A customer abandons their cart, entering a two-stage recovery flow where discounts are withheld until the second touch",
        setup:
          "Connect your e-commerce platform to POST cart abandonment events (cart details, customer info, totals, checkout URL) to the workflow URL.",
        inputFields: [],
      }),

      // 2. No-discount reminder email (AI extract + first email)
      aiNode(
        "2",
        "AI: Extract cart + write first touch",
        280,
        "Extract customer details and cart data from this abandonment event. Write a compelling cart recovery email that does NOT offer any discount — focus on value, social proof, and convenience instead. Respond JSON: {customer_email: string, customer_name: string, cart_total: string, top_item: string, item_count: number, checkout_url: string, email_subject: string, email_body: string, cart_value_num: number}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "cart_total", as: "cart_total", type: "string" },
          { field: "top_item", as: "top_item", type: "string" },
          { field: "item_count", as: "item_count", type: "number" },
          { field: "checkout_url", as: "checkout_url", type: "string" },
          { field: "email_subject", as: "first_touch_subject", type: "string" },
          { field: "email_body", as: "first_touch_body", type: "string" },
          { field: "cart_value_num", as: "cart_value_num", type: "number" },
        ],
      ),

      // 3. Send no-discount reminder
      emailNode(
        "3",
        "Email 1: No-discount reminder",
        520,
        "{customer_email}",
        "{first_touch_subject}",
        "{first_touch_body}\\n\\nComplete your order: {checkout_url}",
      ),

      // 4. Purchase/engagement delay
      n("4", "delayAction", "Wait 24 hours", 760, {
        delayAction: { time: 24, timeFormat: "hours" },
      }),

      // 5. Conversion check condition
      n("5", "condition", "Converted after first touch?", 1000, {
        conditionData: [
          {
            variable: "purchased",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Unique discount generation
      n("6", "runCode", "Generate unique discount code", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a unique, time-limited discount code for second-touch recovery
const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
let code = "SAVE";
for (let i = 0; i < 6; i++) { code += chars[Math.floor(Math.random() * chars.length)]; }
const cartValue = parseFloat(vars.cart_value_num || "0");
const discountPct = cartValue > 150 ? 15 : cartValue > 75 ? 10 : 5;
const expiryDate = new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString();
const estimatedSavings = (cartValue * discountPct / 100).toFixed(2);
const result = {
  discount_code: code,
  discount_pct: String(discountPct),
  expiry_date: expiryDate,
  estimated_savings: estimatedSavings,
  margin_saved_first_touch: "yes"
};
return result;`,
          outputVariables: [
            { name: "discount_code", type: "string" },
            { name: "discount_pct", type: "string" },
            { name: "expiry_date", type: "string" },
            { name: "estimated_savings", type: "string" },
            { name: "margin_saved_first_touch", type: "string" },
          ],
        },
      }),

      // 7. Second-touch discount email
      emailNode(
        "7",
        "Email 2: Exclusive discount offer",
        1480,
        "{customer_email}",
        "Here is {discount_pct}% off your cart, {customer_name}!",
        "Hi {customer_name},\\n\\nWe noticed you are still thinking about {top_item} and your other items.\\n\\nHere is a special {discount_pct}% discount just for you:\\n\\nCode: {discount_code}\\nSavings: ${estimated_savings}\\nExpires: 48 hours\\n\\nApply your code at checkout: {checkout_url}\\n\\nThis code is unique to you and can only be used once.",
      ),

      // 8. Margin savings logging
      sheetsNode(
        "8",
        "Log margin savings data",
        1720,
        "DiscountSecondTouch",
        '[["{customer_email}","{customer_name}","{cart_total}","{cart_value_num}","{top_item}","{margin_saved_first_touch}","{discount_code}","{discount_pct}","{estimated_savings}","{expiry_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "8", "output_1"), // Converted -> log (margin saved, no discount needed)
      e("5", "6", "output_2"), // Not converted -> generate discount
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 386. DYNAMIC PRICING ALERT (7 nodes)
  // ===================================================================

  "Dynamic Pricing Alert": {
    summary:
      "Monitors product pricing and alerts teams and customers when significant changes occur. Runs on a schedule, compares prices against thresholds, evaluates breach significance, notifies the pricing team on Slack, sends wishlist price-drop emails to affected customers, logs changes to Sheets, and aggregates strategy data.",
    nodes: [
      trigger("Scheduled: Price monitoring", "schedule", {
        schedule: "Every 2 hours",
        howItStarts:
          "A scheduled job runs every 2 hours to compare current product prices against stored thresholds and previous prices",
        setup:
          "Configure the schedule to run every 2 hours. Ensure product price data and threshold rules are accessible via your Shopify store or pricing database.",
        inputFields: [],
      }),

      // 2. Threshold comparison
      n("2", "runCode", "Compare prices against thresholds", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare current prices against alert thresholds
const products = JSON.parse(vars.monitored_products || "[]");
const alerts = [];
const thresholds = JSON.parse(vars.price_thresholds || "{}");
// Simulate price check (in production, this pulls from Shopify or pricing DB)
const sampleProduct = products[0] || { title: "Sample Product", price: 49.99, previous_price: 59.99, threshold_pct: 10 };
const currentPrice = parseFloat(sampleProduct.price || "0");
const previousPrice = parseFloat(sampleProduct.previous_price || sampleProduct.price || "0");
const changePct = previousPrice > 0 ? ((previousPrice - currentPrice) / previousPrice * 100).toFixed(1) : "0";
const thresholdPct = parseFloat(sampleProduct.threshold_pct || "10");
const breached = Math.abs(parseFloat(changePct)) >= thresholdPct;
const direction = currentPrice < previousPrice ? "decrease" : currentPrice > previousPrice ? "increase" : "unchanged";
const result = {
  product_title: sampleProduct.title || "Product",
  current_price: String(currentPrice),
  previous_price: String(previousPrice),
  change_pct: changePct,
  change_direction: direction,
  threshold_breached: breached ? "yes" : "no",
  breach_severity: Math.abs(parseFloat(changePct)) > thresholdPct * 2 ? "critical" : breached ? "significant" : "minor",
  affected_product_id: sampleProduct.id || "",
  affected_customer_emails: sampleProduct.wishlist_emails || ""
};
return result;`,
          outputVariables: [
            { name: "product_title", type: "string" },
            { name: "current_price", type: "string" },
            { name: "previous_price", type: "string" },
            { name: "change_pct", type: "string" },
            { name: "change_direction", type: "string" },
            { name: "threshold_breached", type: "string" },
            { name: "breach_severity", type: "string" },
            { name: "affected_product_id", type: "string" },
            { name: "affected_customer_emails", type: "string" },
          ],
        },
      }),

      // 3. Breach significance condition
      n("3", "condition", "Threshold breached?", 520, {
        conditionData: [
          {
            variable: "threshold_breached",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Pricing team Slack alert
      slackNode(
        "4",
        "Slack: Pricing team alert",
        760,
        "*PRICE ALERT — THRESHOLD BREACHED*\\nProduct: {product_title}\\nPrice: ${previous_price} -> ${current_price} ({change_direction})\\nChange: {change_pct}%\\nSeverity: {breach_severity}\\nAffected wishlist customers: {affected_customer_emails}\\n\\nReview pricing strategy and competitor pricing.",
      ),

      // 5. Customer wishlist price-drop email
      emailNode(
        "5",
        "Email: Price drop alert to wishlist",
        1000,
        "{affected_customer_emails}",
        "Price Drop Alert: {product_title} is now ${current_price}!",
        "Great news! A product on your wishlist just dropped in price.\\n\\n{product_title}\\nWas: ${previous_price}\\nNow: ${current_price}\\nYou save: {change_pct}%\\n\\nThis price may not last long. Shop now: https://store.example.com/products/{affected_product_id}",
      ),

      // 6. Price change logging
      sheetsNode(
        "6",
        "Log price change to Sheets",
        1240,
        "PriceChanges",
        '[["{product_title}","{previous_price}","{current_price}","{change_pct}","{change_direction}","{threshold_breached}","{breach_severity}","{affected_customer_emails}"]]',
      ),

      // 7. Strategy aggregation delay
      n("7", "delayAction", "Wait for batch aggregation", 1480, {
        delayAction: { time: 24, timeFormat: "hours" },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // Threshold breached -> alert team
      e("3", "6", "output_2"), // Not breached -> still log
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 387. DYNAMIC SHIPPING UPDATE BOT (8 nodes)
  // ===================================================================

  "Dynamic Shipping Update Bot": {
    summary:
      "Proactive shipping status bot that keeps customers informed throughout delivery. Hydrates tracking data with ETA, generates AI-powered status updates, sends email and SMS notifications, detects delivery delays, sends proactive delay notifications, and logs carrier performance metrics.",
    nodes: [
      trigger("Webhook: Shipping status change", "webhook_app", {
        source: "Shipping Carrier / AfterShip / ShipStation",
        event: "tracking.update",
        howItStarts:
          "A shipping carrier or tracking service reports a status change for a package (shipped, in transit, out for delivery, delivered, exception)",
        setup:
          "Connect your shipping tracking service (AfterShip, ShipStation, carrier webhooks) to POST tracking status updates (tracking number, status, location, ETA, carrier) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Tracking/ETA hydration
      aiNode(
        "2",
        "AI: Extract tracking + status data",
        280,
        "Extract the tracking number, carrier, current status, location, estimated delivery date, order number, and customer details from this shipping status update. Determine if there is a delay compared to the original ETA. Find these fields regardless of payload format. Respond JSON: {tracking_number: string, carrier: string, current_status: string, current_location: string, estimated_delivery: string, original_eta: string, order_number: string, customer_email: string, customer_name: string, customer_phone: string, is_delayed: string, delay_reason: string}",
        "{webhook_payload}",
        [
          { field: "tracking_number", as: "tracking_number", type: "string" },
          { field: "carrier", as: "carrier", type: "string" },
          { field: "current_status", as: "current_status", type: "string" },
          { field: "current_location", as: "current_location", type: "string" },
          {
            field: "estimated_delivery",
            as: "estimated_delivery",
            type: "string",
          },
          { field: "original_eta", as: "original_eta", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "is_delayed", as: "is_delayed", type: "string" },
          { field: "delay_reason", as: "delay_reason", type: "string" },
        ],
      ),

      // 3. AI status update message
      aiNode(
        "3",
        "AI: Write shipping status update",
        520,
        "You are a friendly shipping assistant. Write a clear, reassuring shipping status update for the customer. Adapt the tone based on the status: excited for 'out for delivery', informative for 'in transit', apologetic for delays. Include the tracking link and next expected milestone. Write both an email and a short SMS version. Respond JSON: {email_subject: string, email_body: string, sms_text: string, status_emoji: string, next_milestone: string}",
        "Customer: {customer_name} | Order: {order_number} | Status: {current_status} | Location: {current_location} | ETA: {estimated_delivery} | Carrier: {carrier} | Tracking: {tracking_number} | Delayed: {is_delayed} | Delay reason: {delay_reason}",
        [
          { field: "email_subject", as: "update_subject", type: "string" },
          { field: "email_body", as: "update_body", type: "string" },
          { field: "sms_text", as: "update_sms", type: "string" },
          { field: "next_milestone", as: "next_milestone", type: "string" },
        ],
      ),

      // 4. Email status update
      emailNode(
        "4",
        "Email: Shipping status update",
        760,
        "{customer_email}",
        "{update_subject}",
        "{update_body}\\n\\nTrack your package: https://track.example.com/{tracking_number}\\n\\nNext: {next_milestone}",
      ),

      // 5. SMS status update
      smsNode(
        "5",
        "SMS: Shipping update (Twilio)",
        1000,
        "{customer_phone}",
        "{update_sms}",
      ),

      // 6. Delay detection condition
      n("6", "condition", "Delivery delayed?", 1240, {
        conditionData: [
          {
            variable: "is_delayed",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Proactive delay notification
      emailNode(
        "7",
        "Email: Proactive delay notification",
        1480,
        "{customer_email}",
        "Update on your delivery, {customer_name}",
        "Hi {customer_name},\\n\\nWe want to keep you informed — your order {order_number} is experiencing a slight delay.\\n\\nReason: {delay_reason}\\nOriginal ETA: {original_eta}\\nUpdated ETA: {estimated_delivery}\\nCurrent location: {current_location}\\n\\nWe are monitoring this closely and will notify you of any further changes.\\n\\nTrack your package: https://track.example.com/{tracking_number}\\n\\nWe apologize for the inconvenience and appreciate your patience.",
      ),

      // 8. Carrier performance logging
      sheetsNode(
        "8",
        "Log carrier performance",
        1720,
        "CarrierPerformance",
        '[["{carrier}","{tracking_number}","{order_number}","{current_status}","{current_location}","{estimated_delivery}","{original_eta}","{is_delayed}","{delay_reason}","{customer_email}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // Delayed -> proactive delay notification
      e("6", "8", "output_2"), // On time -> skip to logging
      e("7", "8"),
    ],
  },
};
